// src/actions.tsx
import * as React from "react";
import * as ToggleGroup from "@radix-ui/react-toggle-group";

// src/cn.ts
function cn(...parts) {
  return parts.filter(Boolean).join(" ");
}

// src/actions.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var Button = React.forwardRef(
  ({ variant = "filled", size = "md", icon, className, children, ...rest }, ref) => /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      className: cn("md-btn", `md-btn--${variant}`, size !== "md" && `md-btn--${size}`, className),
      ...rest,
      children: [
        icon,
        children
      ]
    }
  )
);
Button.displayName = "Button";
var IconButton = React.forwardRef(
  ({ variant = "standard", label, className, children, ...rest }, ref) => /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      "aria-label": label,
      className: cn("md-icon-btn", variant !== "standard" && `md-icon-btn--${variant}`, className),
      ...rest,
      children
    }
  )
);
IconButton.displayName = "IconButton";
var Fab = React.forwardRef(
  ({ size = "medium", icon, label, className, children, ...rest }, ref) => /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      "aria-label": children ? void 0 : label,
      className: cn("md-fab", size !== "medium" && `md-fab--${size}`, className),
      ...rest,
      children: [
        icon,
        children
      ]
    }
  )
);
Fab.displayName = "Fab";
function SegmentedButton({ value, onValueChange, options, className, ...rest }) {
  return /* @__PURE__ */ jsx(
    ToggleGroup.Root,
    {
      type: "single",
      value,
      onValueChange: (v) => v && onValueChange(v),
      className: cn("md-segmented", className),
      ...rest,
      children: options.map((o) => /* @__PURE__ */ jsx(ToggleGroup.Item, { value: o.value, "aria-pressed": o.value === value, children: o.label }, o.value))
    }
  );
}

// src/input.tsx
import * as React2 from "react";
import * as RadixSlider from "@radix-ui/react-slider";
import * as RadixSwitch from "@radix-ui/react-switch";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TextField = React2.forwardRef(
  ({ label, help, error, filled, className, id, ...rest }, ref) => {
    const autoId = React2.useId();
    const fieldId = id ?? autoId;
    const helpId = help ? `${fieldId}-help` : void 0;
    return /* @__PURE__ */ jsxs2("div", { className: cn("md-field", filled && "md-field--filled", error && "md-field--error", className), children: [
      /* @__PURE__ */ jsx2("label", { htmlFor: fieldId, children: label }),
      /* @__PURE__ */ jsx2("input", { ref, id: fieldId, "aria-invalid": error || void 0, "aria-describedby": helpId, ...rest }),
      help && /* @__PURE__ */ jsx2("span", { className: "md-field__help", id: helpId, children: help })
    ] });
  }
);
TextField.displayName = "TextField";
var Select = React2.forwardRef(
  ({ label, help, error, options, className, id, ...rest }, ref) => {
    const autoId = React2.useId();
    const fieldId = id ?? autoId;
    return /* @__PURE__ */ jsxs2("div", { className: cn("md-field", error && "md-field--error", className), children: [
      /* @__PURE__ */ jsx2("label", { htmlFor: fieldId, children: label }),
      /* @__PURE__ */ jsx2("select", { ref, id: fieldId, ...rest, children: options.map((o) => /* @__PURE__ */ jsx2("option", { value: o.value, disabled: o.disabled, children: o.label }, o.value)) }),
      help && /* @__PURE__ */ jsx2("span", { className: "md-field__help", children: help })
    ] });
  }
);
Select.displayName = "Select";
function Checkbox({ checked, onCheckedChange, disabled, label, className }) {
  const id = React2.useId();
  const ref = React2.useRef(null);
  React2.useEffect(() => {
    if (ref.current) ref.current.indeterminate = checked === "indeterminate";
  }, [checked]);
  return /* @__PURE__ */ jsxs2("label", { className: cn("md-check", className), htmlFor: id, children: [
    /* @__PURE__ */ jsx2(
      "input",
      {
        ref,
        id,
        type: "checkbox",
        checked: checked === true,
        disabled,
        onChange: (e) => onCheckedChange?.(e.target.checked)
      }
    ),
    label
  ] });
}
function RadioGroup({ value, onValueChange, options, className, ...rest }) {
  const name = React2.useId();
  return /* @__PURE__ */ jsx2(
    "div",
    {
      role: "radiogroup",
      className: cn(className),
      style: { display: "flex", gap: "var(--md-sys-spacing-4)", flexWrap: "wrap" },
      ...rest,
      children: options.map((o) => /* @__PURE__ */ jsxs2("label", { className: "md-check", children: [
        /* @__PURE__ */ jsx2(
          "input",
          {
            type: "radio",
            name,
            value: o.value,
            checked: value === o.value,
            disabled: o.disabled,
            onChange: () => onValueChange(o.value)
          }
        ),
        o.label
      ] }, o.value))
    }
  );
}
function Switch({ checked, onCheckedChange, disabled, className, ...rest }) {
  return /* @__PURE__ */ jsx2(
    RadixSwitch.Root,
    {
      checked,
      onCheckedChange,
      disabled,
      className: cn("md-switch", className),
      ...rest,
      children: /* @__PURE__ */ jsx2("span", { className: "md-switch__track", children: /* @__PURE__ */ jsx2(RadixSwitch.Thumb, { className: "md-switch__thumb" }) })
    }
  );
}
function Slider({
  value,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  marks,
  disabled,
  className,
  ...rest
}) {
  const pct = (v) => (v - min) / (max - min) * 100;
  const stop = (marks ? passi(min, max, step) : [max]).filter((v) => v !== value);
  return /* @__PURE__ */ jsxs2(
    RadixSlider.Root,
    {
      className: cn("md-slider", className),
      value: [value],
      onValueChange: ([v]) => onValueChange(v),
      min,
      max,
      step,
      disabled,
      children: [
        /* @__PURE__ */ jsxs2(RadixSlider.Track, { className: "md-slider__track", children: [
          /* @__PURE__ */ jsx2(RadixSlider.Range, { className: "md-slider__range" }),
          stop.map((v) => /* @__PURE__ */ jsx2(
            "span",
            {
              className: "md-slider__stop",
              "data-oltre": v > value ? "" : void 0,
              style: { "--md-slider-stop": pct(v) }
            },
            v
          ))
        ] }),
        /* @__PURE__ */ jsx2(RadixSlider.Thumb, { className: "md-slider__handle", ...rest })
      ]
    }
  );
}
var MAX_PUNTI = 20;
function passi(min, max, step) {
  const n = Math.round((max - min) / step);
  if (n < 1 || n > MAX_PUNTI) return [max];
  return Array.from({ length: n + 1 }, (_, i) => min + i * step);
}
var Chip = React2.forwardRef(
  ({ kind = "filter", tone = "neutral", selected, icon, className, children, ...rest }, ref) => {
    const classes = cn(
      "md-chip",
      kind === "assist" && "md-chip--assist",
      kind === "status" && "md-chip--status",
      kind === "status" && tone !== "neutral" && `md-chip--${tone}`,
      className
    );
    if (kind === "status") {
      return /* @__PURE__ */ jsxs2("span", { className: classes, children: [
        icon,
        children
      ] });
    }
    return /* @__PURE__ */ jsxs2("button", { ref, className: classes, "aria-selected": kind === "filter" ? !!selected : void 0, ...rest, children: [
      icon,
      children
    ] });
  }
);
Chip.displayName = "Chip";

// src/containment.tsx
import * as React3 from "react";
import * as RadixSeparator from "@radix-ui/react-separator";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Card = React3.forwardRef(
  ({ variant = "outlined", interactive, className, children, ...rest }, ref) => /* @__PURE__ */ jsx3(
    "div",
    {
      ref,
      className: cn("md-card", variant !== "filled" && `md-card--${variant}`, interactive && "md-card--interactive", className),
      ...rest,
      children
    }
  )
);
Card.displayName = "Card";
var List = React3.forwardRef(({ className, children, ...rest }, ref) => /* @__PURE__ */ jsx3("ul", { ref, className: cn("md-list", className), ...rest, children }));
List.displayName = "List";
var ListItem = React3.forwardRef(
  ({ icon, headline, supporting, trailing, interactive, className, ...rest }, ref) => /* @__PURE__ */ jsxs3(
    "li",
    {
      ref,
      className: cn("md-list-item", interactive && "md-list-item--interactive", className),
      ...interactive && {
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }
      },
      ...rest,
      children: [
        icon,
        /* @__PURE__ */ jsxs3("span", { className: "md-list-item__text", children: [
          /* @__PURE__ */ jsx3("span", { className: "md-body-large", children: headline }),
          supporting && /* @__PURE__ */ jsx3("span", { className: "md-list-item__supporting", children: supporting })
        ] }),
        trailing
      ]
    }
  )
);
ListItem.displayName = "ListItem";
function DataTable({ columns, rows, rowKey, onRowClick, className }) {
  const impilata = columns.length >= 4;
  return /* @__PURE__ */ jsx3("div", { className: "md-table-wrap", children: /* @__PURE__ */ jsxs3("table", { className: cn("md-table", impilata && "md-table--impilata", className), children: [
    /* @__PURE__ */ jsx3("thead", { children: /* @__PURE__ */ jsx3("tr", { children: columns.map((c) => /* @__PURE__ */ jsx3("th", { style: c.numeric ? { textAlign: "right" } : void 0, children: c.header }, c.key)) }) }),
    /* @__PURE__ */ jsx3("tbody", { children: rows.map((row, i) => /* @__PURE__ */ jsx3(
      "tr",
      {
        className: onRowClick ? "md-table__row--link" : void 0,
        onClick: onRowClick ? (e) => {
          if (e.target.closest("button, a, select, input, label")) return;
          onRowClick(row, i);
        } : void 0,
        children: columns.map((c) => /* @__PURE__ */ jsx3(
          "td",
          {
            className: cn(c.numeric && "md-num", c.nowrap && "md-nowrap") || void 0,
            "data-label": typeof c.header === "string" && c.header ? c.header : void 0,
            children: row[c.key]
          },
          c.key
        ))
      },
      rowKey ? rowKey(row, i) : i
    )) })
  ] }) });
}
function Divider({ orientation = "horizontal", className }) {
  return /* @__PURE__ */ jsx3(RadixSeparator.Root, { orientation, className: cn("md-divider", className) });
}
var Details = React3.forwardRef(
  ({ summary, className, children, ...rest }, ref) => /* @__PURE__ */ jsxs3("details", { ref, className: cn("md-details", className), ...rest, children: [
    /* @__PURE__ */ jsx3("summary", { children: summary }),
    /* @__PURE__ */ jsx3("div", { className: "md-details__body", children })
  ] })
);
Details.displayName = "Details";

// src/feedback.tsx
import * as React4 from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import * as RadixTooltip from "@radix-ui/react-tooltip";

// src/shapes.ts
var CAMPIONI = 120;
var angoli = Array.from({ length: CAMPIONI }, (_, i) => i / CAMPIONI * Math.PI * 2);
function lobi(n, interno, punta = 1) {
  return angoli.map((t) => {
    const onda = (1 + Math.cos(n * t)) / 2;
    return interno + (1 - interno) * Math.pow(onda, punta);
  });
}
function ellisse(schiacciamento, gradi) {
  const rot = gradi * Math.PI / 180;
  return angoli.map((a) => {
    const t = a - rot;
    return 1 / Math.hypot(Math.cos(t), Math.sin(t) / schiacciamento);
  });
}
var FORME = [
  lobi(10, 0.69, 1.8),
  // SoftBurst: 10 ripetizioni, punte quasi vive
  lobi(9, 0.8, 1),
  //     Cookie9Sided: stella a 9, raggio interno .8, tutto tondo
  lobi(5, 0.87, 1),
  //    Pentagon: pentagono arrotondato
  ellisse(0.55, 45),
  //   Pill
  lobi(8, 0.8, 1.5),
  //   Sunny: stella a 8, raggio interno .8, arrotondamento .15
  lobi(4, 0.4, 1),
  //     Cookie4Sided: quattro lobi profondi
  ellisse(0.64, -45)
  //  Oval: cerchio schiacciato a .64, ruotato di -45°
];
var MORPH_MS = 650;
var MORPH_VALORI = [...FORME, FORME[0]].map(percorso).join(";");
var MORPH_TEMPI = Array.from(
  { length: FORME.length + 1 },
  (_, i) => (i / FORME.length).toFixed(4)
).join(";");
var MORPH_CURVE = Array.from({ length: FORME.length }, () => "0.2 0 0 1").join(";");
function percorso(raggi) {
  const punti = raggi.map((r, i) => {
    const t = angoli[i];
    return `${(r * Math.cos(t)).toFixed(4)} ${(r * Math.sin(t)).toFixed(4)}`;
  });
  return `M${punti[0]}L${punti.slice(1).join("L")}Z`;
}
function anello(centro, raggio, ampiezza, lunghezza) {
  const n = Math.max(1, Math.round(2 * Math.PI * raggio / lunghezza));
  const punti = angoli.map((t) => {
    const r = raggio + ampiezza * Math.cos(n * t);
    return `${(centro + r * Math.cos(t)).toFixed(3)} ${(centro + r * Math.sin(t)).toFixed(3)}`;
  });
  return `M${punti[0]}L${punti.slice(1).join("L")}Z`;
}

// src/tokens.gen.ts
var CIRCOLARE = {
  "size": 40,
  "trackThickness": 4,
  "activeIndicatorThickness": 4,
  "trackActiveIndicatorSpace": 4,
  "activeIndicatorWaveAmplitude": 1.6,
  "activeIndicatorWaveWavelength": 15,
  "withWaveSize": 48
};

// src/feedback.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function Dialog({ open, onOpenChange, title, description, children, actions, className }) {
  return /* @__PURE__ */ jsx4(RadixDialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs4(RadixDialog.Portal, { children: [
    /* @__PURE__ */ jsx4(RadixDialog.Overlay, { className: "md-scrim" }),
    /* @__PURE__ */ jsxs4(RadixDialog.Content, { className: cn("md-dialog", className), children: [
      /* @__PURE__ */ jsx4(RadixDialog.Title, { className: "md-headline-small", children: title }),
      description && /* @__PURE__ */ jsx4(RadixDialog.Description, { className: "md-body-medium", children: description }),
      children,
      actions && /* @__PURE__ */ jsx4("div", { className: "md-dialog__actions", children: actions })
    ] })
  ] }) });
}
function Snackbar({ children, action, className }) {
  return /* @__PURE__ */ jsxs4("div", { className: cn("md-snackbar", className), role: "status", children: [
    /* @__PURE__ */ jsx4("span", { style: { flex: 1 }, children }),
    action && /* @__PURE__ */ jsx4("button", { onClick: action.onClick, children: action.label })
  ] });
}
var SnackbarContext = React4.createContext(null);
var DURATA_MS = 5e3;
var USCITA_MS = 200;
function SnackbarProvider({ children }) {
  const [avvisi, setAvvisi] = React4.useState([]);
  const prossimo = React4.useRef(1);
  const mostra = React4.useCallback((testo, action) => {
    const id = prossimo.current++;
    setAvvisi((prev) => [...prev, { id, testo, action }]);
    window.setTimeout(() => setAvvisi((prev) => prev.map((a) => a.id === id ? { ...a, uscita: true } : a)), DURATA_MS);
    window.setTimeout(() => setAvvisi((prev) => prev.filter((a) => a.id !== id)), DURATA_MS + USCITA_MS);
  }, []);
  return /* @__PURE__ */ jsxs4(SnackbarContext.Provider, { value: mostra, children: [
    children,
    avvisi.length > 0 && /* @__PURE__ */ jsx4("div", { className: "md-snackbar-host", children: avvisi.map((a) => /* @__PURE__ */ jsx4(Snackbar, { action: a.action, className: a.uscita ? "md-snackbar--uscita" : void 0, children: a.testo }, a.id)) })
  ] });
}
function useSnackbar() {
  const mostra = React4.useContext(SnackbarContext);
  return mostra ?? (() => {
  });
}
function Banner({ icon, title, children, tone = "info", action, className }) {
  return /* @__PURE__ */ jsxs4("div", { className: cn("md-banner", tone !== "info" && `md-banner--${tone}`, className), role: tone === "error" ? "alert" : "status", children: [
    icon,
    /* @__PURE__ */ jsxs4("div", { className: "md-banner__testo", children: [
      /* @__PURE__ */ jsx4("div", { className: "md-title-small", children: title }),
      children && /* @__PURE__ */ jsx4("div", { className: "md-body-small", style: { marginTop: 2 }, children })
    ] }),
    action
  ] });
}
function Tooltip({ content, children, side = "top", defaultOpen }) {
  return /* @__PURE__ */ jsx4(RadixTooltip.Provider, { delayDuration: 400, children: /* @__PURE__ */ jsxs4(RadixTooltip.Root, { defaultOpen, children: [
    /* @__PURE__ */ jsx4(RadixTooltip.Trigger, { asChild: true, children }),
    /* @__PURE__ */ jsx4(RadixTooltip.Portal, { children: /* @__PURE__ */ jsx4(RadixTooltip.Content, { className: "md-tooltip", side, sideOffset: 6, children: content }) })
  ] }) });
}
function Progress({ value, className, ...rest }) {
  const indeterminata = value === void 0;
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: cn("md-progress", indeterminata && "md-progress--indeterminata", className),
      role: "progressbar",
      "aria-valuenow": value,
      "aria-valuemin": indeterminata ? void 0 : 0,
      "aria-valuemax": indeterminata ? void 0 : 100,
      style: indeterminata ? void 0 : { "--md-progress-v": `${value}%` },
      ...rest,
      children: [
        /* @__PURE__ */ jsx4("div", { className: "md-progress__traccia" }),
        /* @__PURE__ */ jsx4("div", { className: "md-progress__attiva" }),
        /* @__PURE__ */ jsx4("span", { className: "md-progress__stop" })
      ]
    }
  );
}
var CENTRO = CIRCOLARE.withWaveSize / 2;
var RAGGIO = (CIRCOLARE.withWaveSize - CIRCOLARE.activeIndicatorThickness) / 2 - CIRCOLARE.activeIndicatorWaveAmplitude;
var ONDA = anello(
  CENTRO,
  RAGGIO,
  CIRCOLARE.activeIndicatorWaveAmplitude,
  CIRCOLARE.activeIndicatorWaveWavelength
);
function Spinner({ className, ...rest }) {
  return /* @__PURE__ */ jsxs4(
    "svg",
    {
      className: cn("md-spinner", className),
      viewBox: `0 0 ${CIRCOLARE.withWaveSize} ${CIRCOLARE.withWaveSize}`,
      role: "status",
      ...rest,
      children: [
        /* @__PURE__ */ jsx4("path", { className: "md-spinner__traccia", d: ONDA }),
        /* @__PURE__ */ jsx4("path", { className: "md-spinner__attiva", pathLength: 100, d: ONDA })
      ]
    }
  );
}
function LoadingIndicator({ contained, className, ...rest }) {
  const ridotto = useMovimentoRidotto();
  return /* @__PURE__ */ jsx4("div", { className: cn("md-loading", contained && "md-loading--contenuto", className), role: "status", ...rest, children: /* @__PURE__ */ jsx4("svg", { className: "md-loading__forma", viewBox: "-1 -1 2 2", "aria-hidden": "true", children: /* @__PURE__ */ jsx4("path", { d: percorso(FORME[0]), children: !ridotto && /* @__PURE__ */ jsx4(
    "animate",
    {
      attributeName: "d",
      values: MORPH_VALORI,
      keyTimes: MORPH_TEMPI,
      keySplines: MORPH_CURVE,
      calcMode: "spline",
      dur: `${MORPH_MS * FORME.length}ms`,
      repeatCount: "indefinite"
    }
  ) }) }) });
}
function useMovimentoRidotto() {
  const [ridotto, setRidotto] = React4.useState(false);
  React4.useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setRidotto(mq.matches);
    const aggiorna = () => setRidotto(mq.matches);
    mq.addEventListener("change", aggiorna);
    return () => mq.removeEventListener("change", aggiorna);
  }, []);
  return ridotto;
}
function Skeleton({ width = "100%", height = 14, className }) {
  return /* @__PURE__ */ jsx4("div", { className: cn("md-skeleton", className), style: { width, height }, "aria-hidden": "true" });
}
function Badge({ count, className }) {
  if (count === void 0) return /* @__PURE__ */ jsx4("span", { className: cn("md-badge", "md-badge--dot", className) });
  return /* @__PURE__ */ jsx4("span", { className: cn("md-badge", className), children: count > 99 ? "99+" : count });
}

// src/navigation.tsx
import * as React5 from "react";
import * as RadixTabs from "@radix-ui/react-tabs";

// src/lingua.ts
var traduci = (it) => it.split("|")[0] ?? it;
function impostaTraduzione(fn) {
  traduci = fn;
}
function tr(it) {
  return traduci(it);
}

// src/navigation.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function NavRail({ header, action, children, footer, className }) {
  return /* @__PURE__ */ jsxs5("nav", { className: cn("md-nav-rail", className), "aria-label": tr("Navigazione principale"), children: [
    header && /* @__PURE__ */ jsx5("div", { className: "md-nav-rail__header", children: header }),
    action,
    children,
    footer && /* @__PURE__ */ jsx5("div", { className: "md-nav-rail__footer", children: footer })
  ] });
}
var NavRailItem = React5.forwardRef(
  ({ icon, label, current, secondaria, className, ...rest }, ref) => /* @__PURE__ */ jsxs5("button", { ref, className: cn("md-nav-rail__item", secondaria && "md-nav-rail__item--secondaria", className), "aria-current": current ? "page" : void 0, ...rest, children: [
    /* @__PURE__ */ jsx5("span", { className: "md-nav-rail__icon", children: icon }),
    label
  ] })
);
NavRailItem.displayName = "NavRailItem";
function TopAppBar({ leading, title, subtitle, actions, scrolled, className }) {
  return /* @__PURE__ */ jsxs5("header", { className: cn("md-top-app-bar", scrolled && "md-top-app-bar--scrolled", className), children: [
    leading,
    /* @__PURE__ */ jsxs5("div", { className: "md-top-app-bar__title", children: [
      /* @__PURE__ */ jsx5("div", { className: "md-title-large", children: title }),
      subtitle && /* @__PURE__ */ jsx5("div", { className: "md-body-small", style: { color: "var(--md-sys-color-on-surface-variant)" }, children: subtitle })
    ] }),
    actions && /* @__PURE__ */ jsx5("div", { className: "md-top-app-bar__actions", children: actions })
  ] });
}
function Tabs({ value, onValueChange, tabs, className }) {
  return /* @__PURE__ */ jsx5(RadixTabs.Root, { value, onValueChange, className, children: /* @__PURE__ */ jsx5(RadixTabs.List, { className: "md-tabs", children: tabs.map((t) => /* @__PURE__ */ jsx5(RadixTabs.Trigger, { value: t.value, className: "md-tab", children: t.label }, t.value)) }) });
}
function AppShell({ nav, children, className }) {
  return /* @__PURE__ */ jsxs5("div", { className: cn("md-app", className), children: [
    nav,
    /* @__PURE__ */ jsx5("div", { className: "md-main", children })
  ] });
}

// src/product.tsx
import * as React6 from "react";
import { jsx as jsx6, jsxs as jsxs6 } from "react/jsx-runtime";
function EmptyState({ icon, title, children, action, className }) {
  return /* @__PURE__ */ jsxs6("div", { className: cn("md-empty", className), children: [
    icon,
    /* @__PURE__ */ jsx6("h3", { className: "md-title-medium", children: title }),
    children && /* @__PURE__ */ jsx6("p", { className: "md-body-medium", children }),
    action
  ] });
}
var STATUS_LABEL = {
  creating: "In creazione",
  // La macchina che il provider sta ancora preparando (`BUILD` su OVH):
  // l'IP spesso c'è già, è la macchina a non esserci.
  waiting_ip: "In preparazione",
  waiting_ssh: "In attesa di SSH",
  bootstrapping: "In adozione",
  installing_stack: "Installazione stack",
  verifying: "Verifica|stato",
  ready: "Pronto",
  error: "Fallito",
  decommissioning: "In dismissione",
  released: "Dismesso"
};
var STATUS_TONE = {
  ready: "success",
  released: "neutral",
  error: "error"
};
function StatusChip({ status, label, tone: forzato }) {
  const noto = status in STATUS_LABEL;
  const tone = forzato ?? (noto && STATUS_TONE[status] || (noto ? "running" : "neutral"));
  return /* @__PURE__ */ jsx6(Chip, { kind: "status", tone, children: label ?? (noto ? tr(STATUS_LABEL[status]) : status) });
}
function MetricTile({ label, value, unit, spark, className }) {
  return /* @__PURE__ */ jsxs6("div", { className: cn("md-metric-tile", className), children: [
    /* @__PURE__ */ jsxs6("span", { className: "md-metric-tile__value md-mono", children: [
      value,
      unit && /* @__PURE__ */ jsx6("small", { className: "md-metric-tile__unit", children: unit })
    ] }),
    /* @__PURE__ */ jsx6("span", { className: "md-metric-tile__label", children: label }),
    spark && /* @__PURE__ */ jsx6("span", { className: "md-spark", "aria-hidden": "true", children: spark.map((v, i) => /* @__PURE__ */ jsx6("i", { style: { height: `${Math.max(4, v)}%` } }, i)) })
  ] });
}
function StepProgress({ step, total, label, className }) {
  const pct = total > 0 ? Math.round(step / total * 100) : 0;
  return /* @__PURE__ */ jsxs6("div", { className: cn("md-step-progress", className), children: [
    /* @__PURE__ */ jsxs6("div", { className: "md-step-progress__label md-body-small", children: [
      /* @__PURE__ */ jsx6("span", { children: label ?? tr("Passo {n}").replace("{n}", String(step)) }),
      /* @__PURE__ */ jsxs6("span", { className: "md-mono", children: [
        step,
        "/",
        total
      ] })
    ] }),
    /* @__PURE__ */ jsxs6(
      "div",
      {
        className: "md-progress",
        role: "progressbar",
        "aria-valuenow": step,
        "aria-valuemin": 0,
        "aria-valuemax": total,
        "aria-label": label ?? tr("Avanzamento"),
        style: { "--md-progress-v": `${pct}%` },
        children: [
          /* @__PURE__ */ jsx6("div", { className: "md-progress__traccia" }),
          /* @__PURE__ */ jsx6("div", { className: "md-progress__attiva" }),
          /* @__PURE__ */ jsx6("span", { className: "md-progress__stop" })
        ]
      }
    )
  ] });
}
var SPUNTA = /* @__PURE__ */ jsx6("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "3", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: /* @__PURE__ */ jsx6("path", { d: "M5 12.5l4.5 4.5L19 7.5" }) });
var CROCE = /* @__PURE__ */ jsx6("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "3", strokeLinecap: "round", "aria-hidden": "true", children: /* @__PURE__ */ jsx6("path", { d: "M7 7l10 10M17 7L7 17" }) });
function StepList({ items, compact, className }) {
  return /* @__PURE__ */ jsx6("ol", { className: cn("md-steps", compact && "md-steps--riassunto", className), children: items.map((it, i) => /* @__PURE__ */ jsxs6("li", { className: cn("md-step", `md-step--${it.state}`), "aria-current": it.state === "current" ? "step" : void 0, children: [
    /* @__PURE__ */ jsxs6("span", { className: "md-step__dot", "aria-hidden": "true", children: [
      it.state === "done" && SPUNTA,
      it.state === "failed" && CROCE
    ] }),
    /* @__PURE__ */ jsxs6("span", { className: "md-step__text", children: [
      /* @__PURE__ */ jsx6("span", { className: "md-step__title", children: it.title }),
      !compact && it.detail && /* @__PURE__ */ jsx6("span", { className: "md-step__detail", children: it.detail })
    ] })
  ] }, i)) });
}
function iniziali(name) {
  const parole = name.split("@")[0].split(/[\s._-]+/).filter(Boolean);
  if (parole.length === 0) return "?";
  if (parole.length === 1) return parole[0].slice(0, 2);
  return parole[0][0] + parole[parole.length - 1][0];
}
function Avatar({ name, size = "md", className }) {
  return /* @__PURE__ */ jsx6("span", { className: cn("md-avatar", size === "lg" && "md-avatar--lg", className), "aria-hidden": "true", children: iniziali(name) });
}
function LogStream({ lines, maxHeight = "20rem", empty, className }) {
  const box = React6.useRef(null);
  const attaccato = React6.useRef(true);
  React6.useEffect(() => {
    const el = box.current;
    if (el && attaccato.current) el.scrollTop = el.scrollHeight;
  }, [lines.length]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    attaccato.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };
  if (lines.length === 0) {
    return /* @__PURE__ */ jsx6("div", { className: cn("md-log", "md-log--empty", className), role: "log", "aria-live": "polite", children: /* @__PURE__ */ jsx6("span", { children: empty ?? tr("In attesa delle prime righe\u2026") }) });
  }
  return /* @__PURE__ */ jsx6(
    "div",
    {
      ref: box,
      onScroll,
      className: cn("md-log", className),
      style: { maxHeight },
      role: "log",
      "aria-live": "polite",
      children: lines.map((l) => /* @__PURE__ */ jsxs6(
        "div",
        {
          className: cn("md-log__line", l.stream === "h" && "md-log__line--hub", l.stream === "e" && "md-log__line--err"),
          children: [
            /* @__PURE__ */ jsx6("span", { className: "md-log__seq", children: l.seq }),
            /* @__PURE__ */ jsx6("span", { children: l.line })
          ]
        },
        l.seq
      ))
    }
  );
}
function ServerCard({ name, spec, status, progress, footer, metrics, error, actions, onClick, className }) {
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: cn(
        "md-card",
        status === "error" ? "md-card--outlined md-card--error-outline" : "md-card--outlined",
        onClick && "md-card--interactive",
        className
      ),
      onClick,
      children: [
        /* @__PURE__ */ jsxs6("div", { className: "md-server-card__top", children: [
          /* @__PURE__ */ jsxs6("div", { style: { flex: 1, minWidth: 0 }, children: [
            /* @__PURE__ */ jsx6("div", { className: "md-title-medium", children: name }),
            /* @__PURE__ */ jsx6("div", { className: "md-body-small", style: { color: "var(--md-sys-color-on-surface-variant)" }, children: spec })
          ] }),
          /* @__PURE__ */ jsx6(StatusChip, { status }),
          actions
        ] }),
        progress && /* @__PURE__ */ jsx6(StepProgress, { step: progress.step, total: progress.total, label: progress.label }),
        footer,
        metrics && /* @__PURE__ */ jsx6("div", { className: "md-metric-grid", children: metrics }),
        error && /* @__PURE__ */ jsx6("div", { className: "md-body-small", style: { color: "var(--md-sys-color-error)" }, children: error })
      ]
    }
  );
}
export {
  AppShell,
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Chip,
  DataTable,
  Details,
  Dialog,
  Divider,
  EmptyState,
  Fab,
  IconButton,
  List,
  ListItem,
  LoadingIndicator,
  LogStream,
  MetricTile,
  NavRail,
  NavRailItem,
  Progress,
  RadioGroup,
  SegmentedButton,
  Select,
  ServerCard,
  Skeleton,
  Slider,
  Snackbar,
  SnackbarProvider,
  Spinner,
  StatusChip,
  StepList,
  StepProgress,
  Switch,
  Tabs,
  TextField,
  Tooltip,
  TopAppBar,
  impostaTraduzione,
  iniziali,
  useSnackbar
};
